import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { clearAdminAuth, getAdminTokenForRequest } from '@/utils/adminAuth';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
//
// 后端 admin 接口实际返回 `message` 字段（不是 umi 默认约定的 `errorMessage`），
// 因此 errorThrower 里两者都做兜底，否则 BizError 的 message 永远是空字符串。
interface ResponseStructure {
  success: boolean;
  data?: any;
  code?: string;
  errorCode?: number;
  errorMessage?: string;
  message?: string;
  showType?: ErrorShowType;
}

const AUTHENTICATION_ERROR_CODES = new Set([
  'AUTH_TOKEN_MISSING',
  'AUTH_TOKEN_INVALID',
  'AUTH_TOKEN_EXPIRED',
  'AUTH_SESSION_EXPIRED',
]);
let authRedirectScheduled = false;

export const isAdminAuthenticationFailure = (
  status?: number,
  data?: ResponseStructure,
): boolean => {
  return status === 401 || Boolean(data?.code && AUTHENTICATION_ERROR_CODES.has(data.code));
};

const handleExpiredAdminSession = () => {
  if (authRedirectScheduled) {
    return;
  }

  authRedirectScheduled = true;
  message.warning('登录已过期，请重新登录');
  clearAdminAuth();
  setTimeout(() => {
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = '/user/login?redirect=' + encodeURIComponent(currentPath);
  }, 1000);
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, code, errorCode, errorMessage, message, showType } =
        res as unknown as ResponseStructure;

      // 使用严格判断，只有当success明确为false时才抛出错误
      if (success === false) {
        // 兼容 umi 默认 `errorMessage` 与项目后端约定的 `message` 两种字段名；
        // 双双缺失时给一个能在 console 里识别的兜底，避免 Error 对象 message 为空难定位
        const finalMessage = errorMessage || message || '后端未返回错误信息';
        const error: any = new Error(finalMessage);
        error.name = 'BizError';
        error.info = { code, errorCode, errorMessage: finalMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    //
    // 设计原则：
    // 1. BizError（业务错误，后端 success:false）默认 SILENT，由调用方/页面层独占展示
    //    —— 防止框架 toast 与页面 toast 双弹；后端可显式设置 showType 指定框架代为展示
    // 2. 不论是否展示了 toast，BizError 都必须 re-throw，让调用方 await 收到完整 error
    //    对象（保留 error.info / error.name / error.message），契约与 skipErrorHandler 路径一致
    // 3. HTTP/网络层错误仍由框架统一兜底，因为页面通常无法对其做特化处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (isAdminAuthenticationFailure(undefined, errorInfo)) {
          handleExpiredAdminSession();
          return;
        }
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              // 默认 SILENT：错误向上抛给页面，由页面决定如何提示用户
              break;
          }
        }
        throw error; // BizError 始终向上抛，调用方负责处理
      } else if (error.response) {
        // Axios 的错误：请求已发出且服务器响应了非 2xx 状态码
        const responseData = error.response.data as ResponseStructure | undefined;
        if (isAdminAuthenticationFailure(error.response.status, responseData)) {
          handleExpiredAdminSession();
          return;
        }
        // 其它 HTTP 错误：把后端 JSON body 里的 message 归一化到 error.message，
        // 否则页面层 catch 拿到的 error.message 只是 axios 的 "Request failed with status code N"
        // 而看不到真实后端错误（如 4xx/5xx 响应里携带的 { success:false, message:'...' }）
        const data = error.response.data as any;
        const backendMsg = data?.errorMessage || data?.message;
        if (backendMsg) {
          error.message = backendMsg;
        }
        throw error;
      } else if (error.request) {
        // 请求已发起但没有响应（网络断开 / 超时等）—— 同样交给页面层
        throw error;
      } else {
        // 请求 setup 阶段失败 —— 同样交给页面层
        throw error;
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    async (config: RequestOptions) => {
      const token = await getAdminTokenForRequest(config.url);
      if (!config.headers) {
        config.headers = {};
      }
      (config.headers as Record<string, string>)['X-BO-Client'] = '1';
      if (token) {
        (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      // 不在这里显示错误消息，让具体的业务代码处理
      return response;
    },
  ],
};
